import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../store/api/base';
import Loader from '../components/common/loader';
import AddToCartButton from '../components/cart/addToCartButton';

// ── Reusable product card ──────────────────────────────────────────────────────
const ProductCard = ({ product, quantity, onQuantityChange, onIncrement, onDecrement }) => {
  const getStockBadge = () => {
    if (!product.isInStock)
      return <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Out of Stock</span>;
    if (product.stockStatus === 'LOW_STOCK')
      return <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Low Stock</span>;
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition flex flex-col">
      <Link to={`/product/${product.productId}`}>
        <div className="h-44 overflow-hidden relative bg-gray-50">
          <img
            src={product.thumbnailUrl || product.imageUrl || '/assets/logo/klm-logo.png'}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
          {product.isEcoFriendly && (
            <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">Eco</span>
          )}
          {product.hasBulkPricing && (
            <span className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">Bulk</span>
          )}
          {product.discount > 0 && (
            <span className="absolute bottom-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {product.discount}% OFF
            </span>
          )}
        </div>
        <div className="p-3 flex-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mb-1" title={product.name}>
            {product.name}
          </h3>
          {product.manufacturer && (
            <p className="text-xs text-gray-400 mb-2 truncate">{product.manufacturer}</p>
          )}
          <div className="flex flex-wrap gap-1 mb-2">
            {product.material && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{product.material}</span>
            )}
            {product.thickness?.value && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                {product.thickness.value}{product.thickness.unit}
              </span>
            )}
          </div>
                 <div className="flex items-end justify-between">
            <div>
              {product.hasSizeVariants ? (
                <>
                  <p className="text-[10px] text-gray-400 leading-none mb-0.5">
                    {product.startingSize} · {product.startingPack} pcs/pack
                  </p>
                  <span className="text-xs text-gray-500 font-medium">From </span>
                  <span className="text-base font-bold text-gray-900">
                    ₹{product.price?.toLocaleString('en-IN')}
                  </span>
                  {product.mrp > product.price && (
                    <span className="text-xs text-gray-400 line-through ml-1">
                      ₹{product.mrp?.toLocaleString('en-IN')}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-base font-bold text-gray-900">
                    ₹{product.price?.toLocaleString('en-IN')}
                  </span>
                  {product.mrp > product.price && (
                    <span className="text-xs text-gray-400 line-through ml-1">
                      ₹{product.mrp?.toLocaleString('en-IN')}
                    </span>
                  )}
                </>
              )}
            </div>
            {getStockBadge()}
          </div>
          {!product.hasSizeVariants && product.minOrderQuantity > 1 && (
            <p className="text-xs text-gray-400 mt-1">Min: {product.minOrderQuantity}</p>
          )}
          {product.hasSizeVariants && (
            <p className="text-[10px] text-blue-600 mt-1">Select size on product page</p>
          )}
        </div>
      </Link>
       <div className="px-3 pb-3">
        {product.hasSizeVariants ? (
          <Link
            to={`/product/${product.productId}`}
            className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition"
          >
            Choose Size & Pack
          </Link>
        ) : (
          <>
            {/* Quantity Controls */}
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center border rounded-md">
                  <button
                    onClick={() => onDecrement(product.productId)}
                    disabled={quantity <= (product.minOrderQuantity || 1)}
                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition font-medium text-sm"
                  >−</button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => onQuantityChange(product.productId, e)}
                    min={product.minOrderQuantity || 1}
                    className="w-10 text-center border-x py-1 focus:outline-none text-sm"
                  />
                  <button
                    onClick={() => onIncrement(product.productId)}
                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 transition font-medium text-sm"
                  >+</button>
                </div>
                {(product.minOrderQuantity || 1) > 1 && quantity < (product.minOrderQuantity || 1) && (
                  <span className="text-xs text-red-500">Min: {product.minOrderQuantity}</span>
                )}
                {(product.minOrderQuantity || 1) > 1 && quantity >= (product.minOrderQuantity || 1) && (
                  <span className="text-xs text-green-600">✓ Ready</span>
                )}
              </div>
            </div>
            <AddToCartButton product={product} quantity={quantity} fullWidth={true} showQuantity={false} />
          </>
        )}
      </div>
    </div>
  );
};

// ── Section header ─────────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle, linkTo, linkText, accent = 'green' }) => (
  <div className="flex items-end justify-between mb-6">
    <div>
      <span className={`text-xs font-semibold uppercase tracking-widest text-${accent}-600`}>{subtitle}</span>
      <h2 className="text-2xl font-bold text-gray-900 mt-0.5">{title}</h2>
    </div>
    <Link to={linkTo} className={`text-${accent}-600 hover:underline text-sm font-medium whitespace-nowrap`}>
      {linkText} →
    </Link>
  </div>
);

// ── Product row with loader ────────────────────────────────────────────────────
const ProductRow = ({ products, loading, quantities, onQuantityChange, onIncrement, onDecrement }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Loader size="lg" />
      </div>
    );
  }
  if (!products.length) {
    return <p className="text-center text-gray-400 py-10 text-sm">No products found.</p>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {products.map(p => (
        <ProductCard 
          key={p.productId} 
          product={p} 
          quantity={quantities[p.productId] || 1}
          onQuantityChange={onQuantityChange}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
        />
      ))}
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────
const Home = () => {
  const [agriProducts, setAgriProducts]       = useState([]);
  const [packProducts, setPackProducts]       = useState([]);
  const [newArrivals, setNewArrivals]         = useState([]);
  const [loadingAgri, setLoadingAgri]         = useState(true);
  const [loadingPack, setLoadingPack]         = useState(true);
  const [loadingNew, setLoadingNew]           = useState(true);
  
  // Quantity controls for all products
 const [quantities, setQuantities] = useState({});
  const [agriSlide, setAgriSlide] = useState(0);
  const [packSlide, setPackSlide] = useState(0);

  const agriSlides = [
    {
      bg: '/assets/hero-section-banners/back ground image.png',   // farm field with KLM products
      img: '/assets/hero-section-banners/mulch film.png'  // KLM mulching roll stacks
    },
    {
      bg: '/assets/hero-section-banners/back ground image 2.png',   // aerial green field
      img: '/assets/hero-section-banners/nursery sheets.png'  // white mulching roll
    }
  ];

  const packImages = [
    '/assets/hero-section-banners/courirer bags.png',  // courier bags
    '/assets/hero-section-banners/paper bags.png'   // paper bags (brown + white)
  ];

  useEffect(() => {
    const agriTimer = setInterval(() => {
      setAgriSlide(prev => (prev + 1) % agriSlides.length);
    }, 3500);
    const packTimer = setInterval(() => {
      setPackSlide(prev => (prev + 1) % packImages.length);
    }, 3500);
    return () => {
      clearInterval(agriTimer);
      clearInterval(packTimer);
    };
  }, []);
  useEffect(() => {
    const fetchAll = async () => {
      // All three requests fire in parallel
      const [agriRes, packRes, newRes] = await Promise.allSettled([
        api.get('/products?category=AGRICULTURE_PRODUCTS&limit=6&sortBy=popular'),
        api.get('/products?category=PACKAGING_PRODUCTS&limit=6&sortBy=popular'),
        api.get('/products?sortBy=newest&limit=6'),
      ]);

      if (agriRes.status === 'fulfilled') {
        setAgriProducts(agriRes.value.data.data.products || []);
      }
      setLoadingAgri(false);

      if (packRes.status === 'fulfilled') {
        setPackProducts(packRes.value.data.data.products || []);
      }
      setLoadingPack(false);

      if (newRes.status === 'fulfilled') {
        setNewArrivals(newRes.value.data.data.products || []);
      }
      setLoadingNew(false);
    };

    fetchAll();
  }, []);

  // Initialize quantities when products change
  useEffect(() => {
    const allProducts = [...agriProducts, ...packProducts, ...newArrivals];
    const newQuantities = {};
    allProducts.forEach(product => {
      if (!quantities[product.productId]) {
        newQuantities[product.productId] = product.minOrderQuantity || 1;
      }
    });
    if (Object.keys(newQuantities).length > 0) {
      setQuantities(prev => ({ ...prev, ...newQuantities }));
    }
  }, [agriProducts, packProducts, newArrivals]);

  // Quantity handlers
  const handleQuantityChange = (productId, e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setQuantities(prev => ({
        ...prev,
        [productId]: value
      }));
    }
  };

  const incrementQuantity = (productId) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: prev[productId] + 1
    }));
  };

  const decrementQuantity = (productId) => {
    const allProducts = [...agriProducts, ...packProducts, ...newArrivals];
    const product = allProducts.find(p => p.productId === productId);
    const minQty = product?.minOrderQuantity || 1;
    
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(minQty, prev[productId] - 1)
    }));
  };

  return (
    <div>
  {/* ── Hero: two-panel split ─────────────────────────────────────── */}
<section className="relative grid grid-cols-1 md:grid-cols-2" style={{ minHeight: '420px' }}>

  {/* ── LEFT: Agriculture ── */}
  <div className="relative overflow-hidden" style={{ minHeight: '320px' }}>
    {agriSlides.map((slide, i) => {
      const isActive = agriSlide === i;
      const isPrev   = agriSlide === (i + 1) % agriSlides.length;
      return (
        <div
          key={i}
          className="absolute inset-0"
          style={{
            transition: 'transform 900ms ease-in-out',
            transform: isActive ? 'translateY(0%)' : isPrev ? 'translateY(-100%)' : 'translateY(100%)'
          }}
        >
          <img src={slide.bg} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'rgba(20,60,20,0.52)' }} />
           <img
            src={slide.img}
            alt="Agriculture product"
            style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            height: '85%',
            width: 'auto',
            maxWidth: '55%',
            objectFit: 'contain',
            objectPosition: 'bottom right',
            transition: 'transform 900ms ease-in-out',
            transform: isActive ? 'translateY(0%)' : isPrev ? 'translateY(-110%)' : 'translateY(110%)'
          }}
          />
        </div>
      );
    })}

    {/* Text overlay */}
     <div
      className="relative z-10 flex flex-col justify-center text-white"
      style={{ minHeight: '320px', padding: '28px 24px', width: '55%' }}
    >
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: '#86efac', textTransform: 'uppercase', marginBottom: '10px' }}>
        Agriculture Products
      </span>
       <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', fontWeight: 800, lineHeight: 1.15, marginBottom: '10px' }}>
        Grow More,<br />Waste Less
      </h1>
      <p style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#dcfce7', lineHeight: 1.5, marginBottom: '16px' }}>
        Mulching Film, Nursery Sheets  — engineered for Indian farms.
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Link
          to="/products?category=AGRICULTURE_PRODUCTS"
          style={{ background: 'white', color: '#15803d', padding: '7px 12px', borderRadius: '5px', fontWeight: 700, fontSize: 'clamp(11px, 2.5vw, 13px)', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Shop Agriculture
        </Link>
        <a
          href="https://wa.me/917995004136"
          target="_blank"
          rel="noopener noreferrer"
          style={{ border: '2px solid white', color: 'white', padding: '7px 12px', borderRadius: '5px', fontWeight: 700, fontSize: 'clamp(11px, 2.5vw, 13px)', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Get Quote
        </a>
      </div>
      
    </div>
  </div>

  {/* ── RIGHT: Packaging ── */}
  <div
    className="relative overflow-hidden"
    style={{ minHeight: '320px', background: 'linear-gradient(135deg, #7e22ce 0%, #a855f7 100%)' }}
  >
    {packImages.map((img, i) => {
      const isActive = packSlide === i;
      const isPrev   = packSlide === (i + 1) % packImages.length;
      return (
         <img
          key={i}
          src={img}
          alt="Packaging product"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            height: '95%',
            width: 'auto',
            maxWidth: '60%',
            objectFit: 'contain',
            objectPosition: 'bottom right',
            transition: 'transform 900ms ease-in-out',
            transform: isActive ? 'translateY(0%)' : isPrev ? 'translateY(-110%)' : 'translateY(110%)'
          }}
        />
      );
    })}

    {/* Text — left side */}
    <div
      className="relative z-10 flex flex-col justify-center text-white"
      style={{ minHeight: '320px', padding: '28px 24px', width: '55%' }}
    >
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: '#e9d5ff', textTransform: 'uppercase', marginBottom: '10px' }}>
        Packaging Products
      </span>
      <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', fontWeight: 800, lineHeight: 1.15, marginBottom: '10px' }}>
        Pack Smarter,<br />Ship Faster
      </h1>
      <p style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#f3e8ff', lineHeight: 1.5, marginBottom: '16px' }}>
        Courier bags, Paper bags and custom packaging — built for businesses of every size.
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Link
          to="/products?category=PACKAGING_PRODUCTS"
          style={{ background: 'white', color: '#7e22ce', padding: '7px 12px', borderRadius: '5px', fontWeight: 700, fontSize: 'clamp(11px, 2.5vw, 13px)', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Shop Packaging
        </Link>
        <a
          href="tel:+917995004136"
          style={{ border: '2px solid white', color: 'white', padding: '7px 12px', borderRadius: '5px', fontWeight: 700, fontSize: 'clamp(11px, 2.5vw, 13px)', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Call Us
        </a>
      </div>
      
    </div>
  </div>

  {/* ── Center arrow navigator — sits between the two panels ── */}
  <div
    className="hidden md:flex"
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 20,
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      pointerEvents: 'none'
    }}
  >
    {/* Up arrow */}
    <button
      onClick={() => {
        setAgriSlide(prev => (prev - 1 + agriSlides.length) % agriSlides.length);
        setPackSlide(prev => (prev - 1 + packImages.length) % packImages.length);
      }}
      style={{
        pointerEvents: 'all',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.95)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        transition: 'transform 0.15s, box-shadow 0.15s'
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(0,0,0,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.25)'; }}
    >
         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19V5"/>
        <path d="M5 12l7-7 7 7"/>
      </svg>
    </button>

    

    {/* Down arrow */}
    <button
      onClick={() => {
        setAgriSlide(prev => (prev + 1) % agriSlides.length);
        setPackSlide(prev => (prev + 1) % packImages.length);
      }}
      style={{
        pointerEvents: 'all',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.95)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        transition: 'transform 0.15s, box-shadow 0.15s'
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(0,0,0,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.25)'; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14M5 12l7 7 7-7"/>
      </svg>
    </button>
  </div>

</section>


      {/* ── Trust strip ───────────────────────────────────────────────── */}
      <section className="bg-gray-900 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-300">
            {['Pan-India Delivery 3-7 Days', 'Bulk Discounts Available', '+91 7995004136 '].map(text => (
              <span key={text} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block"></span>
                {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Agriculture Products ───────────────────────────────────────── */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Agriculture Products"
            subtitle="For Farmers"
            linkTo="/products?category=AGRICULTURE_PRODUCTS"
            linkText="View All Agriculture"
            accent="green"
          />
          <ProductRow 
            products={agriProducts} 
            loading={loadingAgri}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            onIncrement={incrementQuantity}
            onDecrement={decrementQuantity}
          />
        </div>
      </section>

      {/* ── Packaging Products ─────────────────────────────────────────── */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Packaging Products"
            subtitle="For Businesses"
            linkTo="/products?category=PACKAGING_PRODUCTS"
            linkText="View All Packaging"
            accent="purple"
          />
          <ProductRow 
            products={packProducts} 
            loading={loadingPack}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            onIncrement={incrementQuantity}
            onDecrement={decrementQuantity}
          />
        </div>
      </section>

      {/* ── New Arrivals ───────────────────────────────────────────────── */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="New Arrivals"
            subtitle="Just Added"
            linkTo="/products?sortBy=newest"
            linkText="View All New"
            accent="green"
          />
          <ProductRow 
            products={newArrivals} 
            loading={loadingNew}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            onIncrement={incrementQuantity}
            onDecrement={decrementQuantity}
          />
        </div>
      </section>

      {/* ── B2B Banner ─────────────────────────────────────────────────── */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-green-400 text-xs font-semibold uppercase tracking-widest">B2B Solutions</span>
              <h2 className="text-3xl font-bold mt-2 mb-3">Bulk Orders Welcome</h2>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Special pricing for farmers, distributors, and businesses. Custom quotations,
                flexible payment terms, and pan-India delivery.
              </p>
              <div className="flex gap-3 flex-wrap">
                <a href="https://wa.me/917995004136 " target="_blank" rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-400 text-white px-6 py-2.5 rounded-md font-semibold text-sm transition">
                  WhatsApp for Quote
                </a>
                <a href="tel:+917995004136 "
                  className="border border-gray-600 text-white px-6 py-2.5 rounded-md font-semibold text-sm hover:border-white transition">
                  +91 7995004136 
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Quality Assured', desc: 'BIS certified products' },
                { label: 'Bulk Discounts', desc: 'Tiered pricing above 50 units' },
                { label: 'Fast Delivery', desc: 'Pan-India in 3-7 days' },
                { label: 'Expert Support', desc: 'Technical guidance included' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-gray-800 rounded-lg p-4">
                  <div className="w-2 h-2 bg-green-400 rounded-full mb-2" />
                  <p className="font-semibold text-sm">{label}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section className="py-10 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-2">Need Help Choosing the Right Product?</h2>
          <p className="text-green-100 text-sm max-w-xl mx-auto mb-6">
            Our team can help you find the perfect solution for your farming or packaging needs.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <a href="tel:+917995004136 "
              className="bg-white text-green-700 px-8 py-3 rounded-md font-semibold text-sm hover:bg-gray-100 transition">
              Call: +91 7995004136 
            </a>
            <a href="https://wa.me/917995004136 " target="_blank" rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md font-semibold text-sm hover:bg-white hover:text-green-700 transition">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;